import { defaultImagingContrast, imagingBodySite, imagingStudyName, } from "./imaging-study-matching.js";
/*
 * Words are compared after folding spellings together, so "abdominal US", "Ultrasound of the
 * abdomen" and "abdomen ultrasound" read the same, and after dropping the words that name the
 * modality, the side or the technique, which never identify WHICH study was asked for.
 */
const MODALITY_PATTERNS = [
    ["XR", /\b(?:xray|radiographs?|radiography|plain films?|kub)\b/],
    ["US", /\b(?:ultrasounds?|ultrasonography|sonograms?|sonography|doppler|u s)\b/],
    ["CT", /\b(?:ct|cta|cat scan|computed tomography)\b/],
    ["MR", /\b(?:mri|mra|mr|magnetic resonance)\b/],
    ["MG", /\b(?:mammograms?|mammography)\b/],
    ["DXA", /\b(?:dexa|dxa|bmd|bone (?:mineral )?density)\b/],
    [
        "FL",
        /\b(?:fluoroscopy|fluoroscopic|barium|swallow study|upper gi|small bowel follow through)\b/,
    ],
    ["NM", /\b(?:nuclear medicine|scintigraphy|bone scan|hida|v q scan|pet)\b/],
];
/** Phrases folded before splitting into words; the order matters. */
const PHRASES = [
    [/\bcxr\b/g, " chest xray "],
    [/\bkub\b/g, " kub abdomen "],
    [/\bx[\s-]?rays?\b/g, " xray "],
    [/\blumbo[\s-]?sacral\b/g, " lumbosacral "],
    [/\b(?:l|ls|l\/s)[\s-]?spine\b/g, " lumbosacral spine "],
    [/\bc[\s-]?spine\b/g, " cervical spine "],
    [/\bt[\s-]?spine\b/g, " thoracic spine "],
    [/\btib(?:ia)?\s*(?:and|\/|&)?\s*fib(?:ula)?\b/g, " tibia fibula "],
];
/** Single words folded to the word a reviewed study uses. */
const SYNONYMS = new Map([
    ["abdominal", "abdomen"],
    ["abdo", "abdomen"],
    ["pelvic", "pelvis"],
    ["lumbar", "lumbosacral"],
    ["si", "sacroiliac"],
    ["ac", "acromioclavicular"],
    ["tm", "temporomandibular"],
    ["tmj", "temporomandibular"],
    ["sc", "sternoclavicular"],
    ["gi", "gastrointestinal"],
    ["kidney", "renal"],
    ["kidneys", "renal"],
    ["ob", "obstetrical"],
    ["obstetric", "obstetrical"],
    ["obstetrics", "obstetrical"],
    ["pregnancy", "obstetrical"],
    ["prenatal", "obstetrical"],
    ["scrotal", "scrotum"],
    ["testis", "testicular"],
    ["testes", "testicular"],
    ["mammogram", "mammography"],
    ["mammograms", "mammography"],
]);
/** Words that name the modality: they choose the modality, never the study within it. */
const MODALITY_WORDS = new Set([
    "xray",
    "radiograph",
    "radiography",
    "plain",
    "film",
    "ultrasound",
    "ultrasonography",
    "sonogram",
    "sonography",
    "ct",
    "cta",
    "cat",
    "computed",
    "tomography",
    "mri",
    "mra",
    "mr",
    "magnetic",
    "resonance",
    "mammography",
    "dexa",
    "dxa",
    "bmd",
    "fluoroscopy",
    "fluoroscopic",
    "nuclear",
    "medicine",
    "scintigraphy",
    "imaging",
    "scan",
]);
/** Words about how the images are taken, which never tell two studies of one site apart. */
const TECHNIQUE_WORDS = new Set([
    "view",
    "pa",
    "ap",
    "lat",
    "lateral",
    "oblique",
    "two",
    "three",
    "four",
    "single",
    "standard",
    "routine",
    "series",
]);
const SIDE_WORDS = new Set([
    "left",
    "right",
    "bilateral",
    "both",
    "bilat",
    "lt",
    "rt",
    "l",
    "r",
    "b",
]);
const STOP_WORDS = new Set([
    "a",
    "an",
    "and",
    "the",
    "of",
    "for",
    "to",
    "in",
    "on",
    "at",
    "or",
    "by",
    "with",
    "without",
    "w",
    "o",
    "no",
    "non",
    "study",
    "exam",
    "examination",
    "test",
    "unspecified",
    "includes",
    "include",
    "including",
    "wks",
    "wk",
    "week",
    "weeks",
    "yr",
    "yrs",
    "year",
    "years",
]);
/**
 * Words about why a study is wanted, never which study: a title made only of these names no
 * study, so its detail is read for one.
 */
const PURPOSE_WORDS = new Set([
    "work",
    "workplace",
    "occupational",
    "employment",
    "employer",
    "job",
    "screening",
    "screen",
    "required",
    "requirement",
    "clearance",
    "preop",
    "pre",
    "op",
    "operative",
    "surveillance",
    "diagnostic",
    "urgent",
    "stat",
    "request",
    "requested",
    "order",
    "tb",
    "immigration",
]);
/** Plural endings folded, so "ribs" is "rib", "sinuses" is "sinus" and "arteries" is "artery". */
function stem(word) {
    if (word.length > 4 && word.endsWith("ies"))
        return `${word.slice(0, -3)}y`;
    if (word.length > 4 && word.endsWith("ses"))
        return word.slice(0, -2);
    if (word.length > 3 && word.endsWith("s") && !/(?:ss|us|is)$/.test(word))
        return word.slice(0, -1);
    return word;
}
/** The text in lower case with spellings folded and dotted letters joined ("S.I." is "si"). */
function foldedText(text) {
    let folded = text.normalize("NFKC").toLowerCase();
    for (let pass = 0; pass < 3; pass += 1)
        folded = folded.replace(/\b([a-z])\.\s?([a-z])\b\.?/g, "$1$2");
    folded = folded.replace(/&/g, " and ").replace(/\+/g, " and ");
    for (const [pattern, replacement] of PHRASES)
        folded = folded.replace(pattern, replacement);
    return folded;
}
function wordsOf(text) {
    return foldedText(text)
        .split(/[^a-z0-9]+/)
        .filter((word) => word.length > 0)
        .map((word) => stem(SYNONYMS.get(word) ?? word));
}
/** A word list as the folded words compare against it: each word, and each as stemmed. */
function folded(words) {
    return new Set([...words].flatMap((word) => [word, stem(word)]));
}
const NOT_IDENTIFYING = folded(new Set([...MODALITY_WORDS, ...TECHNIQUE_WORDS, ...SIDE_WORDS, ...STOP_WORDS]));
const PURPOSE = folded(PURPOSE_WORDS);
/** Words stating the contrast, which a line naming several studies may use without naming one. */
const CONTRAST = folded(new Set(["contrast", "iv", "oral", "enhanced", "un", "unenhanced", "nonenhanced", "wo"]));
/** The words that identify WHICH study: no modality, side, technique, numbers or filler. */
function identifyingWords(text) {
    return new Set(wordsOf(text).filter((word) => !/^\d+$/.test(word) && !NOT_IDENTIFYING.has(word)));
}
/** Every modality the words name. "US" in capitals is ultrasound; the word "us" is not. */
function modalitiesIn(text) {
    const folded = ` ${foldedText(text).replace(/[^a-z0-9]+/g, " ")} `;
    const found = new Set(MODALITY_PATTERNS.filter(([, pattern]) => pattern.test(folded)).map(([modality]) => modality));
    if (/(?:^|[^A-Za-z])US(?:$|[^A-Za-z])/.test(text))
        found.add("US");
    return found;
}
/** The one modality the words name, or undefined when they name none or several. */
export function imagingModalityInText(text) {
    const found = modalitiesIn(text);
    return found.size === 1 ? [...found][0] : undefined;
}
/** Every side the words name: "left knee and right ankle" names left and right. */
function sidesNamedIn(text) {
    const folded = ` ${foldedText(text).replace(/[^a-z0-9()]+/g, " ")} `;
    const sides = new Set();
    if (/\b(?:bilateral|both|bilat)\b|\(b\)/.test(folded))
        sides.add("bilateral");
    if (/\b(?:left|lt)\b|\(l\)/.test(folded))
        sides.add("left");
    if (/\b(?:right|rt)\b|\(r\)/.test(folded))
        sides.add("right");
    return sides;
}
/** The one side the words name, or undefined when they name none. */
export function imagingLateralityInText(text) {
    const sides = sidesNamedIn(text);
    if (sides.has("bilateral") || (sides.has("left") && sides.has("right")))
        return "bilateral";
    if (sides.has("left"))
        return "left";
    if (sides.has("right"))
        return "right";
    return undefined;
}
const WITH_AND_WITHOUT_CONTRAST = /\bwith and without (?:iv )?contrast\b|\bw\/wo\b|\bwith\/without (?:iv )?contrast\b/;
const WITHOUT_CONTRAST = /\b(?:without|w\/o|no|non) (?:iv )?contrast\b|\bnon ?contrast\b|\bun ?enhanced\b|\bnon ?enhanced\b/;
const WITH_CONTRAST = /\bwith (?:iv |oral )?contrast\b|\bcontrast enhanced\b|\bw\/ ?contrast\b/;
function contrastWords(text) {
    return ` ${foldedText(text).replace(/[^a-z0-9/]+/g, " ")} `;
}
/** The contrast the words state, or undefined when they state none. */
export function imagingContrastInText(text) {
    const folded = contrastWords(text);
    if (WITH_AND_WITHOUT_CONTRAST.test(folded))
        return "with_and_without";
    if (WITHOUT_CONTRAST.test(folded))
        return "without";
    if (WITH_CONTRAST.test(folded))
        return "with";
    return undefined;
}
/** How many different contrasts the words state; "with and without contrast" is one. */
function contrastsStated(text) {
    const folded = contrastWords(text);
    const rest = folded.replace(new RegExp(WITH_AND_WITHOUT_CONTRAST.source, "g"), " ");
    return [
        WITH_AND_WITHOUT_CONTRAST.test(folded),
        WITHOUT_CONTRAST.test(rest),
        WITH_CONTRAST.test(rest),
    ].filter(Boolean).length;
}
function scoreCandidates(words, modality, candidates) {
    const scored = [];
    for (const candidate of candidates) {
        if (candidate.modality !== modality)
            continue;
        const site = identifyingWords(candidate.bodySite);
        const name = identifyingWords(candidate.display);
        // A mammogram is of the breast whether or not the words say so.
        const siteNamed = modality === "MG" || [...site].every((word) => words.has(word));
        const nameNamed = name.size > 0 && [...name].every((word) => words.has(word));
        if (!siteNamed && !nameNamed)
            continue;
        const own = new Set([...site, ...name]);
        scored.push({
            candidate,
            site,
            own,
            hits: [...own].filter((word) => words.has(word)).length,
            missing: [...own].filter((word) => !words.has(word) && !(modality === "MG" && site.has(word)))
                .length,
        });
    }
    return scored;
}
function chooseOne(scored) {
    if (scored.length === 1)
        return scored[0];
    // Several fit: only a study whose every own word is in the text can win.
    const complete = scored.filter((entry) => entry.missing === 0);
    if (complete.length === 0)
        return undefined;
    const best = Math.max(...complete.map((entry) => entry.hits));
    const tied = complete.filter((entry) => entry.hits === best);
    // Named equally well, a study a reviewed form carries wins over one only a catalogue lists: it
    // is the one whose box the requisition ticks.
    const onForm = tied.filter((entry) => entry.candidate.onForm === true);
    const winners = tied.length > 1 && onForm.length === 1 ? onForm : tied;
    const winner = winners.length === 1 ? winners[0] : undefined;
    if (winner === undefined)
        return undefined;
    // "Chest and ribs X-ray" names two anatomies: choosing either would drop the other.
    return scored.every((entry) => entry === winner || [...entry.site].every((word) => winner.own.has(word)))
        ? winner
        : undefined;
}
/**
 * The studies a title names when it names several anatomies in full: "Lumbar spine and
 * sacrum/coccyx X-rays" is the lumbosacral spine study and the sacrum and coccyx study. Each
 * anatomy must point at exactly one study, every body-site word of the clinic's studies (`known`)
 * that the title uses must belong to one of them, and every other study that fits must lie within
 * their anatomy. Otherwise none is chosen.
 */
function chooseSeveral(scored, words, known) {
    const complete = scored.filter((entry) => entry.missing === 0 && entry.hits > 0);
    // A study named only as part of a wider one named in full is that one: "spine" is within
    // "lumbar spine".
    const widest = complete.filter((entry) => !complete.some((other) => other.own.size > entry.own.size && [...entry.own].every((word) => other.own.has(word))));
    const chosen = [];
    for (const entry of widest) {
        const same = widest.filter((other) => other.own.size === entry.own.size && [...other.own].every((word) => entry.own.has(word)));
        // Named by the same words, the study a reviewed form carries is the one; else no guess.
        const onForm = same.filter((other) => other.candidate.onForm === true);
        const pick = same.length === 1 ? entry : onForm.length === 1 ? onForm[0] : undefined;
        if (pick === undefined)
            return undefined;
        if (pick === entry)
            chosen.push(entry);
    }
    if (chosen.length < 2)
        return undefined;
    const covered = new Set(chosen.flatMap((entry) => [...entry.own]));
    // A body-site word of another study left over names part of it, so the line is unclear. Other
    // words ("today", "acute injury", or a body part the clinic has no study for) are left out, as
    // one study's title leaves them out.
    if ([...words].some((word) => known.has(word) && !covered.has(word) && !PURPOSE.has(word) && !CONTRAST.has(word)))
        return undefined;
    return scored.every((entry) => chosen.includes(entry) || [...entry.site].every((word) => covered.has(word)))
        ? chosen
        : undefined;
}
/**
 * What a title orders, before any reason it gives: "Chest x-ray for rib pain" orders the chest,
 * not the ribs. A reason follows a study already named, so "X-ray for lumbar spine and
 * sacrum/coccyx" orders what follows its "for".
 */
function orderedPart(title) {
    for (const reason of title.matchAll(/[\s(](?:for|due to|because|after|following|secondary to|to (?:rule out|assess|evaluate|exclude)|rule out|r\/o|query)\b|\s[-–—]\s|\?/gi)) {
        const before = title.slice(0, reason.index);
        if ([...identifyingWords(before)].some((word) => !PURPOSE.has(word)))
            return before;
    }
    return title;
}
/** The modality a plan item names: its title's one modality, or its detail's if the title has none. */
function requestedModality(title, detail) {
    const titleModalities = modalitiesIn(title);
    if (titleModalities.size === 1)
        return [...titleModalities][0];
    return titleModalities.size === 0 ? imagingModalityInText(detail) : undefined;
}
/**
 * The one reviewed study a plan item asks for, or undefined when its words do not point at
 * exactly one. Deliberately conservative: a study is chosen only when the modality is named and
 * the study's own body site (or its own name) is named in full, and when several studies fit,
 * only when one is named more fully than every other and covers their anatomy too. Anything
 * else is left for the clinician to tick on the form rather than guessed. A title naming several
 * anatomies in full is read by `resolveImagingStudies`, as one study for each.
 *
 * The title decides. The detail is read only for what the title leaves out: the modality, the
 * study when the title names no study at all, a side next to the study's own body site, and the
 * contrast.
 */
export function resolveImagingStudy(request, candidates) {
    const detail = request.detail ?? "";
    const modality = requestedModality(request.title, detail);
    if (modality === undefined)
        return undefined;
    const unique = dedupeCandidates(candidates);
    const titleWords = identifyingWords(request.title);
    // The detail is read for the study only when the title names none at all ("X-ray for work
    // screening"). A title that names an anatomy the clinic has no study for ("Left wrist X-ray")
    // is never replaced by another anatomy its detail happens to mention.
    const titleNamesAStudy = [...titleWords].some((word) => !PURPOSE.has(word));
    const scored = titleNamesAStudy
        ? scoreCandidates(titleWords, modality, unique)
        : scoreCandidates(identifyingWords(`${request.title} ${detail}`), modality, unique);
    const chosen = chooseOne(scored);
    if (chosen === undefined)
        return undefined;
    return {
        candidate: chosen.candidate,
        laterality: resolvedLaterality(request.title, detail, chosen),
        contrast: resolvedContrast(`${request.title} ${detail}`, chosen.candidate),
    };
}
/**
 * Every reviewed study a plan item asks for: the one `resolveImagingStudy` finds, or one for each
 * anatomy when the title names several in full ("Lumbar spine and sacrum/coccyx X-rays"), so each
 * study's box is ticked. Empty when the words do not name each study unambiguously; the clinician
 * ticks them on the form instead. Several studies are read only from what the title orders,
 * never from its reasons or the detail, and the line must state at most one contrast.
 */
export function resolveImagingStudies(request, candidates) {
    const one = resolveImagingStudy(request, candidates);
    if (one !== undefined)
        return [one];
    const detail = request.detail ?? "";
    const modality = requestedModality(request.title, detail);
    if (modality === undefined)
        return [];
    const ordered = orderedPart(request.title);
    const words = identifyingWords(ordered);
    if (![...words].some((word) => !PURPOSE.has(word)))
        return [];
    const text = `${request.title} ${detail}`;
    // "CT head without contrast and abdomen with contrast": which goes with which is not guessed.
    if (contrastsStated(text) > 1)
        return [];
    const unique = dedupeCandidates(candidates).filter((candidate) => candidate.modality === modality);
    const known = new Set(unique.flatMap((candidate) => [...identifyingWords(candidate.bodySite)]));
    const chosen = chooseSeveral(scoreCandidates(words, modality, unique), words, known);
    if (chosen === undefined)
        return [];
    // In the order the title names them.
    const named = wordsOf(ordered);
    const position = (entry) => {
        const index = named.findIndex((word) => entry.own.has(word));
        return index < 0 ? named.length : index;
    };
    return [...chosen]
        .sort((left, right) => position(left) - position(right))
        .map((entry) => ({
        candidate: entry.candidate,
        laterality: lateralityAmong(request.title, detail, entry),
        contrast: resolvedContrast(text, entry.candidate),
    }));
}
/** The one side named within two words before one of these body-site words ("right knee"). */
function sideBefore(words, site) {
    const sides = new Set();
    words.forEach((word, index) => {
        if (!site.has(word))
            return;
        for (const neighbour of words.slice(Math.max(0, index - 2), index)) {
            if (neighbour === "left" || neighbour === "lt")
                sides.add("left");
            else if (neighbour === "right" || neighbour === "rt")
                sides.add("right");
            else if (neighbour === "bilateral" || neighbour === "both" || neighbour === "bilat")
                sides.add("bilateral");
        }
    });
    return sides.size === 1 ? [...sides][0] : undefined;
}
function resolvedLaterality(title, detail, chosen) {
    if (!chosen.candidate.lateralityRequired)
        return "not_applicable";
    const inTitle = imagingLateralityInText(title);
    if (inTitle !== undefined)
        return inTitle;
    // A side in the detail counts only right before this study's own body site ("right knee
    // pain"), never elsewhere ("right upper quadrant tenderness").
    return sideBefore(wordsOf(detail), chosen.site) ?? "unknown";
}
/**
 * A study's side on a line naming several: the side right before its own body site ("left knee
 * and right ankle"), else the line's only side ("right shoulder and clavicle"), else a side in
 * the detail right before its site.
 */
function lateralityAmong(title, detail, chosen) {
    if (!chosen.candidate.lateralityRequired)
        return "not_applicable";
    const sides = sidesNamedIn(title);
    return (sideBefore(wordsOf(title), chosen.site) ??
        (sides.size === 1 ? [...sides][0] : undefined) ??
        sideBefore(wordsOf(detail), chosen.site) ??
        "unknown");
}
function resolvedContrast(text, candidate) {
    const stated = imagingContrastInText(text);
    // Preserve the request even if this candidate cannot fulfil it. Facility selection
    // must reject the mismatch instead of silently replacing the clinician's contrast.
    if (stated !== undefined)
        return stated;
    if (candidate.allowedContrast !== undefined) {
        return candidate.allowedContrast.length === 1
            ? (candidate.allowedContrast[0] ?? "unknown")
            : "unknown";
    }
    return defaultImagingContrast(candidate.modality);
}
/**
 * One entry per study. A catalogue item and a form concept with the same code, or with the same
 * modality, body site and name, are the same study; the catalogue item is kept, because it
 * carries the reviewed contrast choices.
 */
function dedupeCandidates(candidates) {
    const kept = [];
    const ordered = [
        ...candidates.filter((candidate) => candidate.allowedContrast !== undefined),
        ...candidates.filter((candidate) => candidate.allowedContrast === undefined),
    ];
    const byKey = new Map();
    for (const candidate of ordered) {
        const keys = [
            `code|${candidate.codeSystem}|${candidate.code.trim().toUpperCase()}`,
            `study|${JSON.stringify([
                candidate.modality,
                imagingBodySite(candidate.bodySite),
                imagingStudyName(candidate.display, candidate.bodySite, "not_applicable"),
            ])}`,
        ];
        const existing = keys.map((key) => byKey.get(key)).find((index) => index !== undefined);
        if (existing !== undefined) {
            const keptCandidate = kept[existing];
            // The same study on a form: the catalogue item kept for it is on that form too.
            if (keptCandidate !== undefined && candidate.onForm === true && keptCandidate.onForm !== true)
                kept[existing] = { ...keptCandidate, onForm: true };
            continue;
        }
        for (const key of keys)
            byKey.set(key, kept.length);
        kept.push(candidate);
    }
    return kept;
}
//# sourceMappingURL=imaging-study-resolution.js.map